import React, { useCallback, useState } from 'react';
import * as Icons from 'react-feather';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { ReactComponent as SortArrow } from '../../../shared/img/chevron-up-down.svg';
import ProductDetailTabTypeModel from './models/ProductDetailTabTypeModel';
import ProductDetailTabTypeModal from './ProductDetailTabTypeModal';
import { getStorageValues } from '../../../shared/CommonFunctions';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { logAuditEvent } from '../../../utils/auditLogger';
import {
  buildProductDetailTabTypeSavePayload,
  mapProductDetailTabTypeFromApi,
} from './utils/mapProductDetailTabType';
import { validateProductDetailTabType } from './productDetailTabTypeValidation';

function ProductDetailTabTypeGrid({
  TabTypeData,
  fetchTabTypes,
  UserPermissions = {},
  moduleName = 'Product Detail Tab Types',
}) {
  const [actionType, setActionType] = useState();
  const [modalIsOpen, setIsOpen] = useState(false);
  const [tabTypeModel, setTabTypeModel] = useState(new ProductDetailTabTypeModel());
  const [filterText, setFilterText] = useState('');
  const [alert, setAlert] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const closeModal = () => {
    setIsOpen(false);
    fetchTabTypes();
  };

  const rows = Array.isArray(TabTypeData)
    ? TabTypeData.filter((item) => {
        const search = filterText.toLowerCase();
        return (
          (item.TabKey && item.TabKey.toLowerCase().includes(search)) ||
          (item.DefaultTitle && item.DefaultTitle.toLowerCase().includes(search)) ||
          (item.ContentType && item.ContentType.toLowerCase().includes(search)) ||
          (item.Placement && item.Placement.toLowerCase().includes(search))
        );
      })
    : [];

  const loadTabTypeDetail = async (tabTypeId) => {
    const response = await axios.get(`${getApiBaseUrl()}/ec-product-detail-tab-type/getById`, {
      params: { TabTypeId: tabTypeId },
    });
    return mapProductDetailTabTypeFromApi(response.data?.data);
  };

  const openActionType = useCallback(
    async (row, name) => {
      const detail = row?.DefaultTitle ? `Tab type: ${row.DefaultTitle}` : '';
      logAuditEvent(moduleName, `${name} Click`, `${name} clicked. ${detail}`.trim());
      setLoadingDetail(true);
      try {
        const full = row?.TabTypeId ? await loadTabTypeDetail(row.TabTypeId) : row;
        setTabTypeModel(full || new ProductDetailTabTypeModel());
        setActionType(name);
        setIsOpen(true);
      } catch (err) {
        console.error(err);
        CreateAlert('Failed to load tab type details', 'Error', setAlert);
      } finally {
        setLoadingDetail(false);
      }
    },
    [moduleName]
  );

  const onSubmit = async (e, data) => {
    if (e.target.name === 'Edit') {
      const payload = buildProductDetailTabTypeSavePayload(data, getStorageValues('userId'));
      if (!validateProductDetailTabType(payload, setAlert)) return;
      try {
        const response = await axios.post(`${getApiBaseUrl()}/ec-product-detail-tab-type/save`, payload);
        const result = response.data?.data;
        if (result?.success === 1) {
          CreateAlert(result.message || 'Tab type updated successfully!!', 'Success', setAlert);
          fetchTabTypes();
          closeModal();
        } else {
          CreateAlert(result?.message || 'Failed to update tab type!!', 'Warning', setAlert);
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to update tab type!!', 'Error', setAlert);
      }
    } else if (e.target.name === 'Delete') {
      try {
        const response = await axios.post(`${getApiBaseUrl()}/ec-product-detail-tab-type/delete`, {
          TabTypeId: tabTypeModel.TabTypeId,
          UserId: getStorageValues('userId'),
        });
        const result = response.data?.data;
        if (result?.success === 1) {
          CreateAlert(result.message || 'Tab type deleted successfully!!', 'Success', setAlert);
          fetchTabTypes();
          closeModal();
        } else {
          CreateAlert(result?.message || 'Failed to delete tab type!!', 'Warning', setAlert);
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to delete tab type!!', 'Error', setAlert);
      }
    }
  };

  const columns = [
    {
      name: (
        <div className="titlename">
          <span>Tab Key</span>
        </div>
      ),
      selector: (row) => row.TabKey,
      cell: (row) => <span title={row.TabKey}>{row.TabKey}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Default Title</span>
        </div>
      ),
      selector: (row) => row.DefaultTitle,
      cell: (row) => <span title={row.DefaultTitle}>{row.DefaultTitle}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Content Type</span>
        </div>
      ),
      selector: (row) => row.ContentType,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Placement</span>
        </div>
      ),
      selector: (row) => row.Placement,
      width: '110px',
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Display Order</span>
        </div>
      ),
      selector: (row) => row.DisplayOrder,
      sortable: true,
      sortIcon: <SortArrow />,
      width: '130px',
    },
  ];

  if (UserPermissions.ViewModule === 1 || UserPermissions.EditModule === 1 || UserPermissions.DeleteModule === 1) {
    columns.push({
      name: <div className="titlename">Actions</div>,
      cell: (row) => (
        <div className="edit-delete-action">
          {UserPermissions.ViewModule === 1 ? (
            <Link className="me-2 p-2" to="" onClick={() => openActionType(row, 'View')}>
              <Icons.Eye className="action-eye" />
            </Link>
          ) : null}
          {UserPermissions.EditModule === 1 ? (
            <Link className="me-2 p-2" to="" onClick={() => openActionType(row, 'Edit')}>
              <Icons.Edit className="feather-edit" />
            </Link>
          ) : null}
          {UserPermissions.DeleteModule === 1 ? (
            <Link className="confirm-text p-2" to="" onClick={() => openActionType(row, 'Delete')}>
              <Icons.Trash2 className="feather-trash-2" />
            </Link>
          ) : null}
        </div>
      ),
      center: 'true',
      width: '150px',
    });
  }

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-top">
            <div className="search-set">
              <div className="search-input">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  id="search-tab-type"
                  type="text"
                  className="form-control form-control-sm custom-search-input"
                  placeholder="Search"
                  aria-label="Search Input"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={rows}
            pagination
            defaultSortField="DisplayOrder"
            sortIcon={<SortArrow />}
            persistTableHead
            progressPending={loadingDetail}
          />
        </div>
      </div>
      <ProductDetailTabTypeModal
        show={modalIsOpen}
        UserAction={actionType}
        onClose={closeModal}
        selectedRow={tabTypeModel}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  );
}

export default ProductDetailTabTypeGrid;
